import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
  Paper,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SendIcon from "@mui/icons-material/Send";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../conf/api";
import { uploadFileInChunks, runWithConcurrency } from "../utils/chunkUploader";
import { VideoSource } from "../Enums/enums";
import { isCategoryMissing } from "../Enums/platform_constants";
import Step1BrandSelection from "./steps/Step1BrandSelection";
import Step2VideoSelection from "./steps/Step2VideoSelection";
import Step3PlatformSource from "./steps/Step3PlatformSource";
import Step4RephrasedContent from "./steps/Step4RephrasedContent";
import Step4Review from "./steps/Step4Review";
import useWizardDraft from "./useWizardDraft";
import { buildPartnerFeedPlatforms, partnerFeedsWithoutPlatforms } from "./partnerFeeds";
import { hasReviewedEdit } from "./batchEditor/batchItems";

const STEPS = [
  "Select Brands & Feeds",
  "Select Video",
  "Platforms & Sources",
  "Rephrase Content",
  "Review & Publish",
];

function isStepValid(step, { selectedBrands, selectedFeeds, brandVideos, brandPlatforms, platformConfigs, brandRephrasedContent, brandFeedsInfo, partnerFeedPlatforms }) {
  if (step === 0) {
    return (
      selectedBrands.length > 0 &&
      selectedBrands.every((b) => (selectedFeeds[b.name] || []).length > 0)
    );
  }
  if (step === 1) {
    return (
      selectedBrands.length > 0 &&
      selectedBrands.every((b) => (brandVideos[b.name] || []).length > 0)
    );
  }
  if (step === 2) {
    // A brand with two or more Partner feeds: each of them needs a platform.
    if (partnerFeedsWithoutPlatforms(selectedBrands, selectedFeeds, brandFeedsInfo, brandPlatforms, partnerFeedPlatforms).length > 0) return false;
    return selectedBrands.every((b) => {
      const platforms = brandPlatforms[b.name] || [];
      if (platforms.length === 0) return false;
      return (brandVideos[b.name] || []).every((v) =>
        platforms.every((p) => {
          const cfg = platformConfigs[b.name]?.[v.unique_key]?.[p];
          if (!cfg) return false;
          const src = cfg.source;
          let sourceResolved = false;
          if (src === VideoSource.ORIGINAL) sourceResolved = true;
          // A compilation's file already lives on its own MRSSVideo row — LOCAL
          // resolves without a per-platform upload.
          else if (src === VideoSource.LOCAL) sourceResolved = v.is_compilation || !!cfg.localVideoPath;
          // An Apply-to-all copy counts only once it has been reviewed.
          else if (src === VideoSource.EDITED) sourceResolved = hasReviewedEdit(cfg);
          else if (src === VideoSource.DUPLICATE) sourceResolved = !!cfg.duplicateOf;
          if (!sourceResolved) return false;
          // Platforms with selectable categories require one to be chosen
          // (a brand default, when configured, is pre-filled into cfg.category).
          if (isCategoryMissing(p, cfg)) return false;
          return true;
        })
      );
    });
  }
  if (step === 3) {
    return selectedBrands.every((b) =>
      (brandVideos[b.name] || []).every((v) =>
        (brandPlatforms[b.name] || []).every(
          (p) => !!brandRephrasedContent?.[b.name]?.[v.unique_key]?.[p]?.title
        )
      )
    );
  }
  return true;
}

export default function BrandPublish() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  const [selectedBrands, setSelectedBrands] = useState([]);
  // { brandName: [feedId, ...] } — user-selected feed IDs per brand
  const [selectedFeeds, setSelectedFeeds] = useState({});
  // { brandName: [{ id, name, feed_type, ... }, ...] } — full feed objects loaded per brand
  const [brandFeedsInfo, setBrandFeedsInfo] = useState({});
  const [brandVideos, setBrandVideos] = useState({});
  const [brandPlatforms, setBrandPlatforms] = useState({});
  const [platformConfigs, setPlatformConfigs] = useState({});
  const [brandRephrasedContent, setBrandRephrasedContent] = useState({});
  const [brandPlatformDefaults, setBrandPlatformDefaults] = useState({});
  // { brandName: { feedId: [platforms] } } — only used when a brand has two or
  // more Partner feeds (partnerFeeds.js); empty for everything else.
  const [partnerFeedPlatforms, setPartnerFeedPlatforms] = useState({});

  const wizardState = { selectedBrands, selectedFeeds, brandVideos, brandPlatforms, platformConfigs, brandRephrasedContent, partnerFeedPlatforms };

  // Survive an accidental refresh: state is mirrored to localStorage/IndexedDB
  // and offered back via the "Resume draft" banner.
  const draftState = { ...wizardState, brandFeedsInfo };
  const { draft, restore, discard, clear: clearDraft } = useWizardDraft(
    draftState,
    activeStep,
    selectedBrands.length > 0
  );

  const handleResumeDraft = async () => {
    const restored = await restore();
    if (!restored) return;
    const d = restored.state;
    setSelectedBrands(d.selectedBrands || []);
    setSelectedFeeds(d.selectedFeeds || {});
    setBrandFeedsInfo(d.brandFeedsInfo || {});
    setBrandVideos(d.brandVideos || {});
    setBrandPlatforms(d.brandPlatforms || {});
    setPlatformConfigs(d.platformConfigs || {});
    setBrandRephrasedContent(d.brandRephrasedContent || {});
    setPartnerFeedPlatforms(d.partnerFeedPlatforms || {});
    setActiveStep(Math.min(restored.step || 0, STEPS.length - 1));
    toast.info("Draft restored. Re-select any local files that couldn't be recovered.");
  };

  // Native "leave site?" prompt while there is unsent work.
  useEffect(() => {
    if (selectedBrands.length === 0 || submitting) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [selectedBrands.length, submitting]);

  const canGoNext = isStepValid(activeStep, { ...wizardState, brandFeedsInfo });
  const isLastStep = activeStep === STEPS.length - 1;

  const handleNext = () => {
    if (canGoNext && !isLastStep) setActiveStep((s) => s + 1);
  };

  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  const handleFeedsChange = (brandName, feedIds) => {
    setSelectedFeeds((prev) => ({ ...prev, [brandName]: feedIds }));
  };

  const handleFeedsInfoChange = (brandName, feeds) => {
    setBrandFeedsInfo((prev) => ({ ...prev, [brandName]: feeds }));
  };

  // Fetch brand-level platform category defaults whenever brand selection changes
  useEffect(() => {
    if (selectedBrands.length === 0) return;
    selectedBrands.forEach(async (brand) => {
      if (brandPlatformDefaults[brand.name]) return;
      try {
        const res = await api.get("/mrss/brand-platform-configs/", {
          params: { brand: brand.name },
        });
        const configs = res.data.results || res.data;
        const defaults = {};
        configs.forEach((cfg) => {
          if (cfg.category || Object.keys(cfg.extra_config || {}).length > 0) {
            defaults[cfg.platform] = {
              category: cfg.category || "",
              extra_config: cfg.extra_config || {},
            };
          }
        });
        setBrandPlatformDefaults((prev) => ({ ...prev, [brand.name]: defaults }));
      } catch (_) {
        // non-fatal
      }
    });
  }, [selectedBrands]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlatformsChange = (brandName, platforms) => {
    setBrandPlatforms((prev) => ({ ...prev, [brandName]: platforms }));
    setPlatformConfigs((prev) => {
      const brandCfg = {};
      const defaults = brandPlatformDefaults[brandName] || {};
      (brandVideos[brandName] || []).forEach((v) => {
        brandCfg[v.unique_key] = {};
        platforms.forEach((p) => {
          const existing = prev[brandName]?.[v.unique_key]?.[p];
          if (existing) {
            brandCfg[v.unique_key][p] = existing;
          } else {
            const def = defaults[p] || {};
            // Compilations have no original_video_url (they're LOCAL uploads),
            // so ORIGINAL would resolve to an empty URL — default to LOCAL,
            // which the scheduler resolves from the compilation's own file.
            brandCfg[v.unique_key][p] = {
              source: v.is_compilation ? VideoSource.LOCAL : VideoSource.ORIGINAL,
              ...(def.category ? { category: def.category } : {}),
              ...(def.extra_config && Object.keys(def.extra_config).length > 0
                ? { extra_config: def.extra_config }
                : {}),
            };
          }
        });
      });
      return { ...prev, [brandName]: brandCfg };
    });
  };

  /**
   * Take one video out of the publish (user request 2026-10-13: removing a
   * WGID in the batch editor left it selected in Steps 2 and 3): it is
   * deselected in Step 2 and its Step 3 cards and titles go with it.
   */
  const handleRemoveVideo = (brandName, videoKey) => {
    setBrandVideos((prev) => ({
      ...prev,
      [brandName]: (prev[brandName] || []).filter((v) => v.unique_key !== videoKey),
    }));
    const drop = (prev) => {
      if (!prev[brandName]?.[videoKey]) return prev;
      const { [videoKey]: _gone, ...rest } = prev[brandName];
      return { ...prev, [brandName]: rest };
    };
    setPlatformConfigs(drop);
    setBrandRephrasedContent(drop);
  };

  const handleConfigChange = (brandName, videoKey, platform, partialConfig) => {
    setPlatformConfigs((prev) => ({
      ...prev,
      [brandName]: {
        ...(prev[brandName] || {}),
        [videoKey]: {
          ...(prev[brandName]?.[videoKey] || {}),
          [platform]: {
            ...(prev[brandName]?.[videoKey]?.[platform] || {}),
            ...partialConfig,
          },
        },
      },
    }));
  };

  const handleRephrasedContentChange = (brandName, videoKey, platforms, content) => {
    setBrandRephrasedContent((prev) => {
      const next = { ...prev };
      if (!next[brandName]) next[brandName] = {};
      if (!next[brandName][videoKey]) next[brandName][videoKey] = {};
      platforms.forEach((p) => {
        next[brandName][videoKey][p] = {
          ...(next[brandName]?.[videoKey]?.[p] || {}),
          ...content,
        };
      });
      return next;
    });
  };

  const buildPlatformConfigs = (localFilePaths = {}, thumbnailFilePaths = {}) => {
    const result = {};
    selectedBrands.forEach((b) => {
      result[b.name] = {};
      const platforms = brandPlatforms[b.name] || [];
      (brandVideos[b.name] || []).forEach((v) => {
        result[b.name][v.unique_key] = {};
        platforms.forEach((p) => {
          const cfg = platformConfigs[b.name]?.[v.unique_key]?.[p] || {};
          const src = cfg.source || VideoSource.ORIGINAL;
          const entry = { source: src };
          if (src === VideoSource.EDITED && cfg.renderPayload) {
            entry.render_payload = cfg.renderPayload;
          }
          if (src === VideoSource.DUPLICATE && cfg.duplicateOf) {
            entry.duplicate_of = cfg.duplicateOf;
          }
          if (
            (src === VideoSource.ORIGINAL || src === VideoSource.LOCAL) &&
            cfg.convertToVertical
          ) {
            entry.convert_to_vertical = true;
          }
          if (src === VideoSource.LOCAL) {
            const filePathKey = `${b.name}__${v.unique_key}__${p}`;
            if (localFilePaths[filePathKey]) {
              entry.local_file_path = localFilePaths[filePathKey];
            }
          }
          const thumbSource = cfg.thumbnailSource || "none";
          entry.thumbnail_source = thumbSource;
          if (thumbSource === "custom") {
            const thumbKey = `${b.name}__${v.unique_key}__${p}__thumb`;
            if (thumbnailFilePaths[thumbKey]) {
              entry.thumbnail_file_path = thumbnailFilePaths[thumbKey];
            }
          }
          const rephrased = brandRephrasedContent?.[b.name]?.[v.unique_key]?.[p];
          if (rephrased?.title) {
            entry.content = {
              title: rephrased.title,
              description: rephrased.description || "",
              keywords: Array.isArray(rephrased.keywords) ? rephrased.keywords : [],
            };
          }
          if (cfg.category) {
            entry.category = cfg.category;
          }
          if (cfg.extra_config && Object.keys(cfg.extra_config).length > 0) {
            entry.extra_config = cfg.extra_config;
          }
          result[b.name][v.unique_key][p] = entry;
        });
      });
    });
    return result;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setUploadProgress(null);
    try {
      // Collect every local file that needs uploading first — video and
      // thumbnail uploads across all brand/video/platform combinations are
      // independent of each other, so they run concurrently (capped, via
      // runWithConcurrency) instead of one after another in a nested
      // for-loop. Uses uploadFileInChunks (uploadParallel's concurrency-
      // capped worker pool), matching the pattern already used correctly in
      // PushToAdminPortal/index.js, instead of ChunkUploader.upload()'s
      // sequential per-chunk path.
      const uploadTasks = [];
      for (const b of selectedBrands) {
        const platforms = brandPlatforms[b.name] || [];
        for (const v of (brandVideos[b.name] || [])) {
          for (const p of platforms) {
            const cfg = platformConfigs[b.name]?.[v.unique_key]?.[p];
            if (cfg?.source === VideoSource.LOCAL && cfg.localFile) {
              uploadTasks.push({
                key: `${b.name}__${v.unique_key}__${p}`,
                target: "video",
                file: cfg.localFile,
                fileType: "video",
                label: `Uploading ${p} video for ${b.name}…`,
              });
            }
            if (cfg?.thumbnailSource === "custom" && cfg.thumbnailFile) {
              uploadTasks.push({
                key: `${b.name}__${v.unique_key}__${p}__thumb`,
                target: "thumbnail",
                file: cfg.thumbnailFile,
                fileType: "image",
                label: `Uploading ${p} thumbnail for ${b.name}…`,
              });
            }
          }
        }
      }

      const localFilePaths = {};
      const thumbnailFilePaths = {};

      if (uploadTasks.length > 0) {
        // Per-task progress aggregated into one label/percentage, since
        // several uploads can be in flight at once.
        const taskProgress = {};
        const activeLabels = new Map();
        const refreshUploadUi = () => {
          const labels = Array.from(activeLabels.values());
          const values = Object.values(taskProgress);
          const percent = values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
          setUploadProgress({
            label:
              labels.length > 0
                ? `${labels[0]}${labels.length > 1 ? ` (+${labels.length - 1} more)` : ""}`
                : `Uploading ${uploadTasks.length} file${uploadTasks.length !== 1 ? "s" : ""}…`,
            percent: Math.round(percent),
          });
        };

        await runWithConcurrency(uploadTasks, async (task) => {
          activeLabels.set(task.key, task.label);
          taskProgress[task.key] = 0;
          refreshUploadUi();
          try {
            const result = await uploadFileInChunks(task.file, task.fileType, {
              onProgress: ({ progress }) => {
                taskProgress[task.key] = progress;
                refreshUploadUi();
              },
            });
            if (task.target === "video") {
              localFilePaths[task.key] = result.file_path;
            } else {
              thumbnailFilePaths[task.key] = result.file_path;
            }
          } finally {
            activeLabels.delete(task.key);
            delete taskProgress[task.key];
            refreshUploadUi();
          }
        });
      }
      setUploadProgress(null);

      const brand_videos = {};
      selectedBrands.forEach((b) => {
        brand_videos[b.name] = (brandVideos[b.name] || []).map((v) => ({
          unique_key: v.unique_key,
          title: v.title || v.video_title || "",
          original_video_url: v.s3_url || v.original_video_url || "",
          original_thumbnail_url: v.original_thumbnail_url || v.portal_thumb || v.thumbnail_url || v.thumbnail || "",
          ...(v.is_compilation ? { is_compilation: true, compilation_id: v.compilation_id } : {}),
          ...(v.is_native_video ? { is_native_video: true, native_video_id: v.native_video_id } : {}),
        }));
      });

      const brand_platforms = {};
      selectedBrands.forEach((b) => {
        brand_platforms[b.name] = brandPlatforms[b.name] || [];
      });

      // Pass the specific feed IDs the user selected per brand
      const brand_feed_ids = {};
      selectedBrands.forEach((b) => {
        brand_feed_ids[b.name] = selectedFeeds[b.name] || [];
      });

      const payload = {
        brand_videos,
        brand_platforms,
        brand_feed_ids,
        // Only brands with two or more Partner feeds appear here; the server
        // treats everything else exactly as before.
        partner_feed_platforms: buildPartnerFeedPlatforms(selectedBrands, selectedFeeds, brandFeedsInfo, brandPlatforms, partnerFeedPlatforms),
        platform_configs: buildPlatformConfigs(localFilePaths, thumbnailFilePaths),
      };

      const res = await api.post("/mrss/brand-publish/", payload);

      clearDraft();
      const { queued, warnings } = res.data;
      toast.success(`${queued} job${queued !== 1 ? "s" : ""} queued successfully!`);
      if (warnings?.length) toast.warn(`${warnings.length} warning(s) — check queue for details.`);
      navigate("/mrss/queue");
    } catch (err) {
      const msg =
        err.response?.data?.error || err.response?.data?.detail || "Failed to queue publish job.";
      setSubmitError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
      setUploadProgress(null);
    }
  };

  const stepContent = [
    <Step1BrandSelection
      selectedBrands={selectedBrands}
      onBrandsSelect={setSelectedBrands}
      selectedFeeds={selectedFeeds}
      onFeedsChange={handleFeedsChange}
      onFeedsInfoChange={handleFeedsInfoChange}
    />,
    <Step2VideoSelection
      selectedBrands={selectedBrands}
      brandVideos={brandVideos}
      onBrandVideosChange={setBrandVideos}
    />,
    <Step3PlatformSource
      selectedBrands={selectedBrands}
      brandVideos={brandVideos}
      brandPlatforms={brandPlatforms}
      platformConfigs={platformConfigs}
      selectedFeeds={selectedFeeds}
      brandFeedsInfo={brandFeedsInfo}
      onPlatformsChange={handlePlatformsChange}
      onConfigChange={handleConfigChange}
      onRemoveVideo={handleRemoveVideo}
      partnerFeedPlatforms={partnerFeedPlatforms}
      onPartnerFeedPlatformsChange={(brandName, map) => setPartnerFeedPlatforms((prev) => ({ ...prev, [brandName]: map }))}
    />,
    <Step4RephrasedContent
      selectedBrands={selectedBrands}
      brandVideos={brandVideos}
      brandPlatforms={brandPlatforms}
      brandRephrasedContent={brandRephrasedContent}
      onRephrasedContentChange={handleRephrasedContentChange}
    />,
    <Step4Review
      selectedBrands={selectedBrands}
      brandVideos={brandVideos}
      brandPlatforms={brandPlatforms}
      platformConfigs={platformConfigs}
      brandRephrasedContent={brandRephrasedContent}
      selectedFeeds={selectedFeeds}
      brandFeedsInfo={brandFeedsInfo}
      partnerFeedPlatforms={partnerFeedPlatforms}
    />,
  ];

  const STEP_HINTS = [
    "Select at least one brand and one feed for every selected brand.",
    "Select at least one video for every selected brand.",
    "Choose platforms, and resolve the video source (and category) for every video.",
    "Add a title for every video on every platform.",
    "",
  ];
  const nextHint = !canGoNext && !isLastStep ? STEP_HINTS[activeStep] : "";

  const actionBar = (
    <Paper
      elevation={0}
      square
      sx={{
        position: "sticky",
        bottom: 0,
        zIndex: (theme) => theme.zIndex.appBar - 1,
        mt: 2,
        mx: { xs: -2, md: 0 },
        px: { xs: 2, md: 0 },
        pt: 1.5,
        pb: { xs: "calc(12px + env(safe-area-inset-bottom))", md: 1.5 },
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      {nextHint && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }} role="status">
          {nextHint}
        </Typography>
      )}
      <Stack direction="row" spacing={1.5} justifyContent="space-between">
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleBack}
          disabled={activeStep === 0 || submitting}
          startIcon={<ArrowBackIcon />}
          sx={{ borderRadius: 2, textTransform: "none", minHeight: { xs: 48, sm: 40 }, flex: { xs: 1, sm: "0 0 auto" }, px: { sm: 3 } }}
        >
          Back
        </Button>

        {isLastStep ? (
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={submitting}
            endIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{ borderRadius: 2, textTransform: "none", minHeight: { xs: 48, sm: 40 }, flex: { xs: 2, sm: "0 0 auto" }, px: { sm: 4 } }}
          >
            {submitting ? (uploadProgress ? "Uploading…" : "Publishing…") : "Publish"}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            onClick={handleNext}
            disabled={!canGoNext}
            endIcon={<ArrowForwardIcon />}
            sx={{ borderRadius: 2, textTransform: "none", minHeight: { xs: 48, sm: 40 }, flex: { xs: 2, sm: "0 0 auto" }, px: { sm: 4 } }}
          >
            Next
          </Button>
        )}
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto", width: "100%" }}>
      <Box component="header" mb={{ xs: 2, md: 3 }}>
        <Typography variant="h4" component="h1" fontWeight={600} sx={{ fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
          Brand Publish
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Publish a video to multiple brands with per-platform video sources.
        </Typography>
      </Box>

      {draft && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" onClick={handleResumeDraft}>
                Resume
              </Button>
              <Button color="inherit" size="small" onClick={discard}>
                Discard
              </Button>
            </Stack>
          }
        >
          You have an unfinished publish draft from {new Date(draft.savedAt).toLocaleString()}.
        </Alert>
      )}

      {isMobile ? (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1}>
            <Typography variant="subtitle2" fontWeight={600}>
              {STEPS[activeStep]}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Step {activeStep + 1} of {STEPS.length}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={((activeStep + 1) / STEPS.length) * 100}
            aria-label={`Step ${activeStep + 1} of ${STEPS.length}: ${STEPS[activeStep]}`}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Paper>
      ) : (
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      <Box mb={2} sx={{ minWidth: 0 }}>
        {stepContent[activeStep]}
      </Box>

      {uploadProgress && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2 }} role="status" aria-live="polite">
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ wordBreak: "break-word" }}>
            {uploadProgress.label}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <LinearProgress variant="determinate" value={uploadProgress.percent} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
            <Typography variant="caption" color="text.secondary">
              {uploadProgress.percent}%
            </Typography>
          </Stack>
        </Paper>
      )}
      {submitError && (
        <Alert severity="error" role="alert" sx={{ mt: 2 }}>
          {submitError}
        </Alert>
      )}
      {actionBar}
    </Box>
  );
}
